import { PlanPermission, useAuthorizationChecker } from 'authorization-scope';
import { usePopupFormManager } from 'features/nekst-widgets';
import { Plan, usePlansService } from 'nekst-api';
import { ButtonSize, isReactNative, OptionSelectionButton } from 'ui-builder';

import React from 'react';
import { RenamePlanForm } from './RenamePlanForm';
import { ChangePlanTypeForm } from './ChangePlanTypeForm';
import { DeletePlanForm } from './DeletePlanForm';
import { CopyPlanForm } from './CopyPlanForm';
import { SharePlanForm } from './SharePlanForm';
import { UpdatePlanSetForm } from './UpdatePlanSetForm';
import { CopyToGlobalPlanForm } from './CopyToGlobalPlanForm';

enum PlanAction {
  RENAME = 'rename',
  DELETE = 'delete',
  COPY = 'copy',
  SHARE = 'share',
  EXPORT = 'export',
  CHANGE_TYPE = 'change_type',
  CHANGE_SET = 'change_set',
  COPY_TO_GLOBAL_PLAN = 'copy_to_global_plan',
}

export function EditPlanButton(
  props: {
    plan: Plan,
    goToPlansListFunc: () => void,
    goToPlanFunc: (plan: Plan) => void,
    customButton?: React.FC<{ onClick: () => void }>,
  }
) {

  const { isGranted } = useAuthorizationChecker();

  const popupManager = usePopupFormManager();

  const planService = usePlansService();

  return (
    <OptionSelectionButton<PlanAction>
      text="Edit"
      options={[
        isGranted(PlanPermission.UPDATE) && {
          value: PlanAction.RENAME,
          label: 'Rename',
          onSelected: () => {
            popupManager.openForm(
              <RenamePlanForm id={props.plan.id} />,
              true,
            );
          },
        },
        isGranted(PlanPermission.UPDATE_TYPE) && {
          value: PlanAction.CHANGE_TYPE,
          label: 'Change type',
          onSelected: async () => {
            const plan = await planService.getById(props.plan.id);

            popupManager.openForm(
              <ChangePlanTypeForm plan={plan} />,
            );
          }
        },
        ...isGranted(PlanPermission.UPDATE) && props.plan._setupArea?.alias === 'internal' ? [
          {
            value: PlanAction.CHANGE_SET,
            label: 'Assign detail box set',
            onSelected: async () => {
              popupManager.openForm(
                <UpdatePlanSetForm id={props.plan.id} />
              )
            },
          }
        ] : [],
        isGranted(PlanPermission.DELETE) && {
          value: PlanAction.DELETE,
          label: 'Delete workflow',
          onSelected: () => {
            popupManager.openForm(
              <DeletePlanForm id={props.plan.id} afterSubmitFunc={async () => {
                props.goToPlansListFunc();
              }} />,
            );
          }
        },
        {
          value: PlanAction.COPY,
          label: 'Copy workflow',
          onSelected: () => {
            popupManager.openForm(
              <CopyPlanForm
                plan={props.plan}
                afterSubmitFunc={async (plan) => props.goToPlanFunc(plan)}
              />,
              true,
            );
          }
        },
        isGranted(PlanPermission.SHARE) && {
          value: PlanAction.SHARE,
          label: 'Share workflow',
          onSelected: () => {
            popupManager.openForm(
              <SharePlanForm id={props.plan.id} />,
              true,
            );
          }
        },
        isGranted(PlanPermission.UPDATE) && !isReactNative() && {
          value: PlanAction.EXPORT,
          label: 'Export workflow',
          onSelected: () => {
            planService.exportToCsv(props.plan.id);
          }
        },
        isGranted(PlanPermission.COPY_TO_GLOBAL_PLAN) && {
          value: PlanAction.COPY_TO_GLOBAL_PLAN,
          label: 'Copy as global workflow',
          onSelected: () => {
            popupManager.openForm(
              <CopyToGlobalPlanForm id={props.plan.id} />
            )
          }
        }
      ]}
      size={ButtonSize.MEDIUM}
      customButton={props.customButton}
    />
  );
}
